export interface TimeZoneGroup {
  /** The IANA region prefix: America, Europe, Asia, …; "Other" for zones without one. */
  region: string;
  zones: string[];
}

const OTHER = 'Other';

let cachedZones: readonly string[] | null = null;

/** Every IANA zone this browser knows, sorted; computed once because the list runs to ~400 names. */
export function supportedTimeZones(): readonly string[] {
  if (cachedZones) return cachedZones;
  const zones = Intl.supportedValuesOf('timeZone');
  if (!zones.includes('UTC')) zones.push('UTC');
  cachedZones = zones.sort((a, b) => a.localeCompare(b));
  return cachedZones;
}

/** Zones grouped by their region prefix, regions in alphabetical order with "Other" last. */
export function timeZoneGroups(zones: readonly string[] = supportedTimeZones()): TimeZoneGroup[] {
  const byRegion = new Map<string, string[]>();
  for (const zone of [...zones].sort((a, b) => a.localeCompare(b))) {
    const slash = zone.indexOf('/');
    const region = slash > 0 ? zone.slice(0, slash) : OTHER;
    const list = byRegion.get(region) ?? [];
    list.push(zone);
    byRegion.set(region, list);
  }
  return [...byRegion.entries()]
    .sort(([a], [b]) => (a === OTHER ? 1 : b === OTHER ? -1 : a.localeCompare(b)))
    .map(([region, zones]) => ({ region, zones }));
}

/** True for a zone `Intl` accepts; anything else makes reports and exports fail on the server. */
export function isTimeZone(value: string): boolean {
  if (supportedTimeZones().includes(value)) return true;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** "America/New_York" → "New York"; the region is already the group heading. */
export function timeZoneLabel(zone: string): string {
  const slash = zone.indexOf('/');
  return (slash > 0 ? zone.slice(slash + 1) : zone).replaceAll('_', ' ');
}

/** "UTC−05:00" for the zone right now, so two names in one region are easy to tell apart. */
export function timeZoneOffset(zone: string, at = new Date()): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(at);
    const name = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (!name) return null;
    return name === 'GMT' ? 'UTC+00:00' : name.replace(/^GMT/, 'UTC').replace('-', '−');
  } catch {
    return null;
  }
}
